import React from "react";
import { presetToCssVars } from "../lib/themes";

/**
 * Mobile-phone-frame live preview of a theme.
 * Shows hero + 1 category strip + 2 menu items + bottom action bar.
 * Tiny, fast, accurate to PublicMenu rendering.
 */
export default function ThemePreview({ preset, restaurantName = "Mein Restaurant", compact = false }) {
  const vars = presetToCssVars(preset);
  const isList = preset.layout === "list";
  const isCards = preset.layout === "cards";

  const heroBg = preset.hero === "none" ? preset.bg : preset.hero;
  const isDark = preset.id !== "minimal_cafe" && preset.id !== "default";
  const sampleImg = "https://images.unsplash.com/photo-1546069901-ba9599a7e63c?w=400&q=70&auto=format&fit=crop";

  const cardBg = preset.cardStyle === "flat" ? "transparent" :
                 preset.cardStyle === "bordered" ? preset.surface :
                 preset.cardStyle === "floating" ? preset.surface : preset.surface;
  const cardBorder = preset.cardStyle === "bordered" ? `1px solid ${preset.border}` :
                     preset.cardStyle === "floating" ? `1px solid ${preset.border}` : "none";
  const cardShadow = preset.cardStyle === "floating" ? "0 8px 24px rgba(0,0,0,0.18)" : "none";

  return (
    <div
      className={`relative overflow-hidden ${compact ? "w-full" : "mx-auto"} ${preset.id === "steakhouse" ? "rounded-md" : "rounded-2xl"}`}
      style={{
        ...vars,
        background: `linear-gradient(180deg, ${preset.bg} 0%, ${preset.bgEnd} 100%)`,
        color: preset.text,
        fontFamily: preset.fontBody,
        height: compact ? 280 : 520,
      }}
    >
      {/* HERO */}
      <div
        className="relative flex items-end p-3"
        style={{
          height: compact ? "44%" : "38%",
          background: preset.showHeroImage
            ? `${heroBg}, url(${sampleImg}) center/cover`
            : preset.bg,
          backgroundBlendMode: preset.showHeroImage && preset.hero !== "none" ? "normal" : undefined,
        }}
      >
        {preset.showHeroImage && (
          <div
            className="absolute inset-0"
            style={{
              backgroundImage: `url(${sampleImg})`,
              backgroundSize: "cover",
              backgroundPosition: "center",
              opacity: 0.55,
            }}
          />
        )}
        <div
          className="absolute inset-0"
          style={{
            background: preset.hero === "none" ? "transparent" : preset.hero,
          }}
        />
        <div className="relative">
          <div
            className="text-[7px] uppercase font-bold mb-0.5"
            style={{ letterSpacing: "0.3em", color: preset.primary }}
          >
            {preset.id === "steakhouse" ? "PREMIUM" : preset.id === "elegant_turkish" ? "ANATOLIA" : "DIGITAL MENU"}
          </div>
          <div
            className={`leading-[0.9] ${preset.id === "steakhouse" ? "text-xl font-black uppercase" : "text-base"}`}
            style={{
              fontFamily: preset.fontHeading,
              fontWeight: preset.headingWeight,
              letterSpacing: preset.headingTracking,
              color: preset.heroText,
            }}
          >
            {restaurantName}
          </div>
        </div>
      </div>

      {/* CATEGORY STRIP */}
      <div className="flex gap-1.5 px-3 py-2 overflow-hidden" style={{ borderBottom: `1px solid ${preset.border}` }}>
        {["Vorspeisen", "Hauptgerichte", "Desserts"].map((c, i) => (
          <span
            key={c}
            className={`text-[8px] font-bold px-2 py-0.5 ${preset.btnStyle === "block" ? "uppercase" : ""}`}
            style={{
              borderRadius: preset.categoryStyle === "tabs" ? "0" : preset.btnRadius,
              background: i === 0 ? preset.primary : "transparent",
              color: i === 0 ? (preset.id === "dark_luxury" ? "#0a0807" : preset.id === "minimal_cafe" ? "#fff" : "#fff") : preset.muted,
              border: i === 0 ? "none" : `1px solid ${preset.border}`,
            }}
          >
            {c}
          </span>
        ))}
      </div>

      {/* MENU ITEMS */}
      <div className={`p-3 space-y-2 ${isCards ? "grid grid-cols-2 gap-2 space-y-0" : ""}`}>
        {[
          { name: preset.id === "elegant_turkish" ? "Adana Kebap" : preset.id === "minimal_cafe" ? "Flat White" : preset.id === "steakhouse" ? "RIBEYE 350g" : preset.id === "modern_neon" ? "Neon Burger" : "Tagesgericht", price: preset.id === "minimal_cafe" ? "4,20" : "18,90" },
          { name: preset.id === "elegant_turkish" ? "Baklava" : preset.id === "minimal_cafe" ? "Cappuccino" : preset.id === "steakhouse" ? "TOMAHAWK" : "Spezialität", price: preset.id === "minimal_cafe" ? "3,80" : "12,50" },
        ].map((it, i) => (
          <div
            key={i}
            className={`flex items-center gap-2 ${isCards ? "flex-col items-start p-1.5" : "p-1.5"}`}
            style={{
              background: cardBg,
              border: cardBorder,
              boxShadow: cardShadow,
              borderRadius: preset.radius,
            }}
          >
            {(isList || isCards) && (
              <div
                className={`${isCards ? "w-full aspect-[4/3]" : "w-10 h-10"} shrink-0 rounded-md`}
                style={{
                  background: `url(${sampleImg}) center/cover`,
                  borderRadius: preset.radius,
                  filter: preset.id === "steakhouse" ? "grayscale(0.3)" : "none",
                }}
              />
            )}
            <div className="flex-1 min-w-0 w-full">
              <div className="flex items-baseline justify-between gap-2">
                <div
                  className={`text-[10px] truncate ${preset.id === "steakhouse" ? "uppercase font-black" : "font-bold"}`}
                  style={{
                    fontFamily: preset.fontHeading,
                    fontWeight: preset.id === "minimal_cafe" ? 600 : preset.id === "steakhouse" ? 900 : 700,
                    color: preset.text,
                  }}
                >
                  {it.name}
                </div>
                <div
                  className={`text-[10px] shrink-0 ${preset.priceStyle === "mono" ? "font-mono" : "font-bold"}`}
                  style={{
                    color: preset.priceStyle === "default" ? preset.text : preset.primary,
                    fontFamily: preset.priceStyle === "impact" ? "Impact, sans-serif" : undefined,
                  }}
                >
                  {it.price}€
                </div>
              </div>
              <div className="text-[8px] leading-tight mt-0.5 truncate" style={{ color: preset.muted }}>
                {preset.id === "minimal_cafe" ? "Single Origin · Oat" : "Hausgemacht · frisch"}
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* STICKY ACTION BAR */}
      <div className="absolute left-2 right-2 bottom-2 flex gap-1.5 justify-center">
        <button
          className={`text-[8px] font-bold px-2.5 py-1.5 ${preset.btnStyle === "block" ? "uppercase" : ""}`}
          style={{
            borderRadius: preset.btnRadius,
            background: preset.btnStyle === "outline" ? "transparent" : preset.primary,
            color: preset.btnStyle === "outline" ? preset.primary : (preset.id === "dark_luxury" ? "#0a0807" : "#fff"),
            border: preset.btnStyle === "outline" ? `1px solid ${preset.primary}` : "none",
            boxShadow: preset.btnStyle === "outline" ? "none" : `0 6px 14px ${preset.primary}40`,
          }}
        >
          🔔 Kellner
        </button>
        <button
          className={`text-[8px] font-bold px-2.5 py-1.5 ${preset.btnStyle === "block" ? "uppercase" : ""}`}
          style={{
            borderRadius: preset.btnRadius,
            background: "transparent",
            color: preset.text,
            border: `1px solid ${preset.border}`,
          }}
        >
          💶 Rechnung
        </button>
      </div>
    </div>
  );
}
